import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useToast} from '@/matic/components/ui/toast';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {trackProductEvent} from '@/matic/lib/product-observability';
import {getFilesApi} from '../api/files-api';
import {filesKeys} from '../query-keys';
import {
  MULTIPART_THRESHOLD_BYTES,
  type FileAssociation,
  type FileCategory,
  type FilesListResponse,
  type ManagedFile,
  type UpdateFileInput,
  type CreateFileFromRelayInput,
} from '../types/files-types';

type UploadAndCreateArgs = {
  file: globalThis.File;
  name: string;
  categoryId?: string;
  associations?: Array<{objectTypeId: string; objectRecordId: string | null}>;
  cui?: boolean;
  /** Reports upload progress as a fraction 0–1 (parts + finalize). */
  onProgress?: (fraction: number) => void;
};

type UpdateFileArgs = {
  id: string;
  input: UpdateFileInput;
  successToastTitle?: string | null;
};

export function useFileMutations(anchorAssociationRef?: string) {
  const tenantId = useRequiredTenantId();
  const queryClient = useQueryClient();
  const {toast} = useToast();
  const api = getFilesApi();

  const invalidateLists = async () => {
    await queryClient.invalidateQueries({
      queryKey: filesKeys.files.listPrefix(tenantId),
    });
  };

  // Optimistically mutate a file inside every files list cache entry.
  const patchFileInLists = (
    fileId: string,
    updater: (f: ManagedFile) => ManagedFile
  ) => {
    const entries = queryClient.getQueriesData<FilesListResponse>({
      queryKey: filesKeys.files.listPrefix(tenantId),
    });
    for (const [key, data] of entries) {
      if (!data) continue;
      queryClient.setQueryData<FilesListResponse>(key, {
        ...data,
        items: data.items.map((f) => (f.id === fileId ? updater(f) : f)),
      });
    }
  };

  // Mark the files lists stale WITHOUT refetching now, so a needs-attention
  // file that just gained its required association stays visible in the triage
  // feed until the user refreshes (it would otherwise drop on the next refetch).
  const markFileListsStale = () => {
    void queryClient.invalidateQueries({
      queryKey: filesKeys.files.listPrefix(tenantId),
      refetchType: 'none',
    });
  };

  const uploadAndCreate = useMutation({
    async mutationFn({
      file,
      name,
      categoryId,
      associations,
      cui,
      onProgress,
    }: UploadAndCreateArgs) {
      const contentType = file.type || 'application/octet-stream';

      // Large files go through S3 multipart (resumable, per-part, lifts the
      // single-PUT 5 GB ceiling). Multipart finalize takes a single anchor
      // association, matching the single-PUT staged-create semantics.
      if (file.size >= MULTIPART_THRESHOLD_BYTES) {
        const anchor = associations?.[0];
        if (!anchor) {
          throw new Error(
            'An object association is required to upload a file.'
          );
        }

        return api.uploadLargeFile(
          file,
          {
            name,
            originalFilename: file.name,
            contentType,
            association: anchor,
            categoryId,
            cui,
          },
          onProgress
        );
      }

      const {url, fileId} = await api.requestUploadUrl(
        file.name,
        contentType,
        file.size
      );
      await api.uploadToPresignedUrl(url, file, onProgress, contentType);
      return api.createFileFromStaging({
        fileId,
        name,
        originalFilename: file.name,
        contentType,
        categoryId,
        associations,
        cui,
      });
    },
    async onSuccess(created) {
      await invalidateLists();
      toast({title: 'File uploaded successfully', variant: 'success'});
      trackProductEvent('resource.created', {
        resourceType: 'file',
        fileId: created.id,
        trigger: 'upload',
      });
    },
    onError() {
      toast({title: 'Failed to upload file', variant: 'error'});
    },
  });

  const createFromRelay = useMutation({
    mutationFn: async (input: CreateFileFromRelayInput) =>
      api.createFileFromRelay(input),
    async onSuccess(created) {
      await invalidateLists();
      toast({title: 'File created from attachment', variant: 'success'});
      trackProductEvent('resource.created', {
        resourceType: 'file',
        fileId: created.id,
        trigger: 'relay_attachment',
      });
    },
    onError() {
      toast({title: 'Failed to create file from attachment', variant: 'error'});
    },
  });

  // Rename / recategorize is optimistic — the card reads from the list cache,
  // so we patch every list entry (and the detail) up front for instant
  // feedback, then reconcile against the server on settle.
  const updateFile = useMutation({
    mutationFn: async ({id, input}: UpdateFileArgs) =>
      api.updateFile(id, input),
    async onMutate({id, input}) {
      const listsKey = filesKeys.files.listPrefix(tenantId);
      const detailKey = filesKeys.files.detail(id, tenantId);
      await queryClient.cancelQueries({queryKey: listsKey});
      await queryClient.cancelQueries({queryKey: detailKey});

      const previousLists = queryClient.getQueriesData<FilesListResponse>({
        queryKey: listsKey,
      });
      const previousDetail = queryClient.getQueryData<ManagedFile>(detailKey);

      const patch = (f: ManagedFile): ManagedFile => ({
        ...f,
        ...(input.name === undefined ? {} : {name: input.name}),
        ...('categoryId' in input
          ? {categoryId: input.categoryId ?? null}
          : {}),
      });

      for (const [key, data] of previousLists) {
        if (!data) continue;
        queryClient.setQueryData<FilesListResponse>(key, {
          ...data,
          items: data.items.map((f) => (f.id === id ? patch(f) : f)),
        });
      }

      if (previousDetail) {
        queryClient.setQueryData<ManagedFile>(detailKey, patch(previousDetail));
      }

      return {previousLists, previousDetail};
    },
    onError(_error, _variables, context) {
      for (const [key, data] of context?.previousLists ?? []) {
        queryClient.setQueryData(key, data);
      }

      if (context?.previousDetail) {
        queryClient.setQueryData(
          filesKeys.files.detail(context.previousDetail.id, tenantId),
          context.previousDetail
        );
      }

      toast({title: 'Failed to update file', variant: 'error'});
    },
    onSuccess(updated, variables) {
      queryClient.setQueryData(
        filesKeys.files.detail(updated.id, tenantId),
        updated
      );
      if (variables.successToastTitle !== null) {
        toast({
          title: variables.successToastTitle ?? 'File updated',
          variant: 'success',
        });
      }

      trackProductEvent('resource.updated', {
        resourceType: 'file',
        fileId: updated.id,
        fieldsChanged: Object.keys(variables.input),
      });
    },
    async onSettled() {
      await invalidateLists();
    },
  });

  const softDeleteFile = useMutation({
    mutationFn: async (id: string) => api.softDeleteFile(id),
    async onSuccess(_data, id) {
      queryClient.removeQueries({
        queryKey: filesKeys.files.detail(id, tenantId),
      });
      await invalidateLists();
      toast({title: 'File deleted', variant: 'success'});
      trackProductEvent('resource.updated', {
        resourceType: 'file',
        fileId: id,
        deleted: true,
      });
    },
    onError() {
      toast({title: 'Failed to delete file', variant: 'error'});
    },
  });

  const addAssociation = useMutation({
    mutationFn: async ({
      fileId,
      objectTypeId,
      objectRecordId,
    }: {
      fileId: string;
      objectTypeId: string;
      objectRecordId: string | null;
    }) => api.addAssociation(fileId, objectTypeId, objectRecordId),
    async onMutate({fileId, objectTypeId, objectRecordId}) {
      await queryClient.cancelQueries({
        queryKey: filesKeys.files.listPrefix(tenantId),
      });
      const detailKey = filesKeys.files.detail(fileId, tenantId);
      await queryClient.cancelQueries({queryKey: detailKey});
      const previousLists = queryClient.getQueriesData<FilesListResponse>({
        queryKey: filesKeys.files.listPrefix(tenantId),
      });
      const previousDetail = queryClient.getQueryData<ManagedFile>(detailKey);
      // Append a placeholder association so the chip appears immediately; names
      // are null until the server responds (the chip resolves them on its own).
      const tempAssoc: FileAssociation = {
        id: `temp-${globalThis.crypto.randomUUID()}`,
        objectTypeId,
        objectRecordId,
        objectTypeName: null,
        objectRecordName: null,
      };
      patchFileInLists(fileId, (f) => ({
        ...f,
        associations: [...f.associations, tempAssoc],
      }));
      // Also patch the detail cache so panels subscribed to useFile(id) update.
      if (previousDetail) {
        queryClient.setQueryData<ManagedFile>(detailKey, {
          ...previousDetail,
          associations: [...previousDetail.associations, tempAssoc],
        });
      }

      return {previousLists, previousDetail};
    },
    onSuccess(newAssoc, {fileId}) {
      // Replace the temp placeholder with the real association (display names).
      const replaceTemp = (assocs: FileAssociation[]) => [
        ...assocs.filter((a) => !a.id.startsWith('temp-')),
        newAssoc,
      ];
      patchFileInLists(fileId, (f) => ({
        ...f,
        associations: replaceTemp(f.associations),
      }));
      // Patch the detail cache directly so the panel shows the real assoc
      // before the background refetch completes.
      const detailKey = filesKeys.files.detail(fileId, tenantId);
      queryClient.setQueryData<ManagedFile>(detailKey, (prev) =>
        prev ? {...prev, associations: replaceTemp(prev.associations)} : prev
      );
      void queryClient.invalidateQueries({queryKey: detailKey});
      toast({title: 'Association added', variant: 'success'});
      trackProductEvent('resource.updated', {
        resourceType: 'file',
        fileId,
        action: 'association_added',
        objectTypeId: newAssoc.objectTypeId,
      });
    },
    onError(_error, {fileId}, context) {
      for (const [key, data] of context?.previousLists ?? []) {
        queryClient.setQueryData(key, data);
      }

      if (context?.previousDetail) {
        queryClient.setQueryData(
          filesKeys.files.detail(fileId, tenantId),
          context.previousDetail
        );
      }

      toast({title: 'Failed to add association', variant: 'error'});
    },
    async onSettled() {
      // Keep the file in the feed even though it now has its required
      // association — it drops on the next real refresh.
      markFileListsStale();
    },
  });

  const removeAssociation = useMutation({
    mutationFn: async ({fileId, assocId}: {fileId: string; assocId: string}) =>
      api.removeAssociation(fileId, assocId),
    async onSuccess(_data, {fileId, assocId}) {
      await queryClient.invalidateQueries({
        queryKey: filesKeys.files.detail(fileId, tenantId),
      });
      // Invalidate lists so removing the anchor object's association
      // drops the file from the current tab immediately.
      await invalidateLists();
      toast({title: 'Association removed', variant: 'success'});
      trackProductEvent('resource.updated', {
        resourceType: 'file',
        fileId,
        action: 'association_removed',
        associationId: assocId,
      });
    },
    onError() {
      toast({title: 'Failed to remove association', variant: 'error'});
    },
  });

  // Create + rename are optimistic — they almost always succeed and the
  // category list is cheap to roll back. Delete is intentionally NOT optimistic
  // (see `deleteCategory`): the backend decides whether a soft-delete is safe,
  // so we wait for its response before mutating the list.
  const categoriesKey = filesKeys.categories.all(tenantId);

  const createCategory = useMutation({
    mutationFn: async ({name, sortOrder}: {name: string; sortOrder?: number}) =>
      api.createCategory(name, sortOrder),
    async onMutate({name, sortOrder}) {
      await queryClient.cancelQueries({queryKey: categoriesKey});
      const previous = queryClient.getQueryData<FileCategory[]>(categoriesKey);
      const activeCount = (previous ?? []).filter(
        (c) => c.deletedAt === null
      ).length;
      const optimistic: FileCategory = {
        id: `optimistic-${globalThis.crypto.randomUUID()}`,
        name,
        sortOrder: sortOrder ?? activeCount,
        deletedAt: null,
      };
      queryClient.setQueryData<FileCategory[]>(categoriesKey, [
        ...(previous ?? []),
        optimistic,
      ]);
      return {previous};
    },
    onError(_error, _variables, context) {
      if (context?.previous) {
        queryClient.setQueryData(categoriesKey, context.previous);
      }

      toast({title: 'Failed to create label', variant: 'error'});
    },
    async onSettled() {
      await queryClient.invalidateQueries({queryKey: categoriesKey});
    },
  });

  const updateCategory = useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: {name?: string; sortOrder?: number};
    }) => api.updateCategory(id, input),
    async onMutate({id, input}) {
      await queryClient.cancelQueries({queryKey: categoriesKey});
      const previous = queryClient.getQueryData<FileCategory[]>(categoriesKey);
      queryClient.setQueryData<FileCategory[]>(categoriesKey, (current) =>
        (current ?? []).map((c) =>
          c.id === id
            ? {
                ...c,
                ...(input.name === undefined ? {} : {name: input.name}),
                ...(input.sortOrder === undefined
                  ? {}
                  : {sortOrder: input.sortOrder}),
              }
            : c
        )
      );
      return {previous};
    },
    onError(_error, _variables, context) {
      if (context?.previous) {
        queryClient.setQueryData(categoriesKey, context.previous);
      }

      toast({title: 'Failed to update label', variant: 'error'});
    },
    async onSettled() {
      await queryClient.invalidateQueries({queryKey: categoriesKey});
    },
  });

  const deleteCategory = useMutation({
    mutationFn: async (id: string) => api.deleteCategory(id),
    async onSuccess() {
      await queryClient.invalidateQueries({queryKey: categoriesKey});
      toast({title: 'Label removed', variant: 'success'});
    },
    onError() {
      toast({title: 'Failed to remove label', variant: 'error'});
    },
  });

  return {
    uploadAndCreate,
    createFromRelay,
    updateFile,
    softDeleteFile,
    addAssociation,
    removeAssociation,
    createCategory,
    updateCategory,
    deleteCategory,
  };
}
